import * as React from 'react';
import {
  TypeImplementation,
  withSystem,
  Expressions,
} from '@packages/systems/wfdl';
import {
  TText,
  EText,
  TRecord,
  ERecord,
  EHole,
  Expression,
} from '@packages/systems/wfdl-ast';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {Select, Option} from '@packages/systems/design-system/WebflowSelect';
import {
  SearchInputPopoverList,
  useSharedFlowContext,
} from '@packages/systems/flows/shared';

import {styledDiv, emotionStyled, tokens} from '@packages/utilities/styled';

import {stripAll} from '@packages/utilities/contextual-expression';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const userSearchType = withSystem(
  {isFullWidth: true},
  TRecord({name: TText, id: TText, email: TText})
);
type UserRecord = {
  id: string;
  email: string;
  name: string;
};

const selectSiteShortName = (state: any) => state.SiteDataStore.shortName;
const getSearchableText = (userItem: UserRecord) =>
  `${userItem.name || ''} ${userItem.email || ''}`;
const getDisplayableText = (record: {[key: string]: Expression}) => {
  // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
  const nameText = record ? Expressions.getText(record.name) || '' : '';
  // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
  const emailText = record ? Expressions.getText(record.email) || '' : '';

  return nameText ? nameText : emailText;
};

const NameCircle = styledDiv(
  ({theme: {colors, gutters, fontSizes, fontWeights, control}}) => ({
    borderRadius: '50%',
    width: control.height,
    height: control.height,
    padding: gutters.x8,

    background: colors.backgroundLight,
    color: colors.white,
    textAlign: 'center',

    font: fontSizes.copy,
    fontWeight: fontWeights.subheading,

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }),
  {
    hover: ({theme: {colors}}) => ({
      background: colors.successBackground,
    }),
  }
);

const MENU_ITEM_OVERFLOW = 180;
const EmailMuted = styledDiv(({theme}) => ({
  fontSize: theme.fontSizes.copy,
  color: theme.colors.muted,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: MENU_ITEM_OVERFLOW,
}));
const NameLine = styledDiv(({nameProvided}) => ({
  fontStyle: nameProvided ? 'none' : 'italic',
}));
const NameMenuItem = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'row',
  columnGap: tokens.space[2],
  alignItems: 'center',
});

/**
 * The field that is below "View users" in the Delete/Update blocks
 */
export const UserSearchAliasType = TypeImplementation.Alias({
  system: {bindUIVariant: 'custom'},
  type: userSearchType,
  render: function UserSearchAlias({expr, dispatch}) {
    const strippedExpr = stripAll(expr);
    const record = Expressions.getRecord(strippedExpr);

    const siteShortName = useDesignerSelector(selectSiteShortName);

    const {useUsers} = useSharedFlowContext();
    // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
    const userQueryResult = useUsers({
      siteShortName,
      debounceMs: 80,
      query: {
        page: 0,
        pageSize: 25,
        sort: '-created-on',
        usysGroups: [],
        search: '',
        status: [],
      },
    });

    const users = userQueryResult.data?.users;

    const searchList = React.useMemo<Array<UserRecord>>(() => {
      const list = users
        ? users.map((user) => ({
            id: user._id,
            email: user.commonFields.email,
            name: user.commonFields.name,
          }))
        : [];
      return list;
    }, [users]);

    const onUserItemSelect = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'userItem' implicitly has an 'any' type.
      (userItem) => {
        dispatch(
          updateExpressionAction(
            ERecord({
              name: EText(userItem.name),
              id: EText(userItem.id),
              email: EText(userItem.email),
            })
          )
        );
      },
      [dispatch]
    );

    const onBindingChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'optionId' implicitly has an 'any' type. | TS7006 - Parameter 'meta' implicitly has an 'any' type.
      (optionId, meta) => {
        if (
          meta.changeType === 'changeEnd' &&
          optionId &&
          optionId === 'unlink'
        ) {
          dispatch(updateExpressionAction(EHole()));
        }
      },
      [dispatch]
    );

    if (record) {
      const displayableText = getDisplayableText(record);
      return (
        <>
          <Select
            id="bound-item-select"
            data-automation-id="bound-item-select"
            value={displayableText}
            onChange={onBindingChange}
            aria-label={`Selected item: ${displayableText}`}
          >
            <Option
              key={`bound-item-select-${displayableText}`}
              value={displayableText}
              label={displayableText}
              data-automation-id={`bound-item-select-${displayableText}`}
            />
            <Option
              key="bound-item-unlink-option"
              value="unlink"
              label="Unlink item"
              data-automation-id="bound-item-unlink-option"
            />
          </Select>
        </>
      );
    }

    const renderMatch = ({
      match,
      preselected,
    }: {
      match: UserRecord;
      preselected: UserRecord | null;
    }) => {
      const matchIsHovered = match === preselected;
      // If name is available, we'll render it
      // along with the email
      const nameProvided = match.name.trim().length > 0;
      const [firstName, lastName] = nameProvided
        ? match.name.split(' ')
        : ['', ''];

      return (
        <NameMenuItem>
          <NameCircle hover={matchIsHovered}>
            {/**
             * If there's a first name, render the first character
             * and the first character of the last name.
             *
             * If no last name, then we'll just render nothing for the last name
             *
             * Last, if none available, render the first letter of the email
             */}
            {/* @ts-expect-error - TS18048 - 'firstName' is possibly 'undefined'. */}
            {nameProvided ? firstName[0] : ''}
            {/* @ts-expect-error - TS18048 - 'lastName.length' is possibly 'undefined'. | TS18048 - 'lastName' is possibly 'undefined'. */}
            {nameProvided && (lastName?.length > 0 ? lastName[0] : '')}
            {/* @ts-expect-error - TS2532 - Object is possibly 'undefined'. */}
            {!nameProvided && match.email[0].toUpperCase()}
          </NameCircle>
          <div>
            <NameLine nameProvided={nameProvided}>
              {nameProvided ? match.name : '-'}
            </NameLine>
            <EmailMuted>{match.email}</EmailMuted>
          </div>
        </NameMenuItem>
      );
    };

    return (
      <SearchInputPopoverList
        getSearchableText={getSearchableText}
        itemTypeLabel="users"
        searchList={searchList}
        onItemSelect={onUserItemSelect}
        renderMatch={renderMatch}
      />
    );
  },
});
